import { useEffect, useMemo, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { MENU, ITEM_BY_ID, formatEuro, type MenuItem } from './menu-data'
import { buildOrderPayload } from './order-payload'

type View = 'menu' | 'details' | 'ticket'

type CartLine = {
  qta: number
  /** modificatori selezionati, ciascuno con quante unità lo hanno (1..qta) */
  mods: { ingredientId: number; count: number }[]
}
type Cart = Map<number, CartLine>

export type ExpandedRow = {
  id: number
  qta: number
  ingredienti: { id: number; qta: number }[]
  /** nome da mostrare: il label del modificatore se 'variant', altrimenti il nome piatto */
  name: string
  /** riga secondaria in corsivo (es. "con Grappa", "senza aglio"); assente se 'variant' */
  note?: string
}

/** Quante unità della linea hanno già un modificatore assegnato */
function assignedCount(line: CartLine): number {
  return line.mods.reduce((sum, m) => sum + m.count, 0)
}

/**
 * Espande il carrello in righe d'ordine: ogni modificatore con il suo
 * contatore diventa una riga (es. 3 caffè corretti, 1 grappa + 2 sambuca →
 * "1× con Grappa" + "2× con Sambuca"); le unità senza modificatore restano
 * come riga a parte, se ce ne sono. Nome e nota della riga dipendono da
 * `modDisplay` dell'articolo: 'variant' sostituisce il nome col label
 * ("Amaro del Capo"), 'attr'/'exclusion' lo mettono in minuscolo come nota
 * ("naturale", "senza aglio"), 'con' (default) lo prefissa ("con Grappa").
 */
function expandCart(cart: Cart): ExpandedRow[] {
  const out: ExpandedRow[] = []
  for (const [id, line] of cart) {
    if (line.qta <= 0) continue
    const item = ITEM_BY_ID.get(id)
    let assigned = 0
    for (const m of line.mods) {
      if (m.count <= 0) continue
      const label = item?.modifiers?.find((x) => x.ingredientId === m.ingredientId)?.label
      if (!item || !label) continue
      const row = { id, qta: m.count, ingredienti: [{ id: m.ingredientId, qta: 1 }] }
      const style = item.modDisplay ?? 'con'
      if (style === 'variant') out.push({ ...row, name: label })
      else if (style === 'con') out.push({ ...row, name: item.name, note: `con ${label}` })
      else out.push({ ...row, name: item.name, note: label.charAt(0).toLowerCase() + label.slice(1) })
      assigned += m.count
    }
    const rest = line.qta - assigned
    if (rest > 0) out.push({ id, qta: rest, ingredienti: [], name: item?.name ?? '' })
  }
  return out
}

const STEP_ARIA = (name: string) => ({ minus: `Riduci quantità di ${name}`, plus: `Aumenta quantità di ${name}` })

export function PreordiniPage() {
  const [view, setView] = useState<View>('menu')
  const [cart, setCart] = useState<Cart>(new Map())
  const [numeroTavolo, setNumeroTavolo] = useState('')
  const [tavolo, setTavolo] = useState('')
  const [notaOrdine, setNotaOrdine] = useState('')
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [payload, setPayload] = useState('')
  const mainRef = useRef<HTMLDivElement>(null)

  const rows = useMemo(() => expandCart(cart), [cart])

  const total = useMemo(
    () => rows.reduce((sum, r) => sum + (ITEM_BY_ID.get(r.id)?.price ?? 0) * r.qta, 0),
    [rows],
  )

  const itemCount = rows.reduce((n, r) => n + r.qta, 0)

  const changeQty = (id: number, delta: number) => {
    setCart((prev) => {
      const next = new Map(prev)
      const line = next.get(id) ?? { qta: 0, mods: [] }
      const qta = Math.max(0, line.qta + delta)
      if (qta === 0) next.delete(id)
      else {
        // riducendo la quantità, i contatori in eccesso si azzerano
        // partendo dall'ultimo modificatore aggiunto
        const mods = line.mods.map((m) => ({ ...m }))
        let excess = assignedCount(line) - qta
        for (let i = mods.length - 1; i >= 0 && excess > 0; i--) {
          const cut = Math.min(mods[i].count, excess)
          mods[i].count -= cut
          excess -= cut
        }
        next.set(id, { qta, mods: mods.filter((m) => m.count > 0) })
      }
      return next
    })
  }

  /** attiva un modificatore partendo da 1 unità (il contatore sale da lì) */
  const addMod = (id: number, ingredientId: number) => {
    setCart((prev) => {
      const next = new Map(prev)
      const line = next.get(id)
      if (!line) return next
      const free = line.qta - assignedCount(line)
      if (free <= 0) return next
      next.set(id, { ...line, mods: [...line.mods, { ingredientId, count: 1 }] })
      return next
    })
  }

  const removeMod = (id: number, ingredientId: number) => {
    setCart((prev) => {
      const next = new Map(prev)
      const line = next.get(id)
      if (!line) return next
      next.set(id, { ...line, mods: line.mods.filter((m) => m.ingredientId !== ingredientId) })
      return next
    })
  }

  /** aggiorna il contatore di un modificatore (0 = lo rimuove) */
  const setModCount = (id: number, ingredientId: number, count: number) => {
    setCart((prev) => {
      const next = new Map(prev)
      const line = next.get(id)
      if (!line) return next
      const others = assignedCount(line) - (line.mods.find((m) => m.ingredientId === ingredientId)?.count ?? 0)
      const clamped = Math.min(Math.max(0, count), line.qta - others)
      const mods =
        clamped === 0
          ? line.mods.filter((m) => m.ingredientId !== ingredientId)
          : line.mods.map((m) => (m.ingredientId === ingredientId ? { ...m, count: clamped } : m))
      next.set(id, { ...line, mods })
      return next
    })
  }

  /** piatti con modificatori obbligatori non ancora coperti per intero */
  const missingMods = useMemo(
    () =>
      [...cart.entries()]
        .filter(([id, line]) => {
          const item = ITEM_BY_ID.get(id)
          return item?.modRequired && line.qta > 0 && assignedCount(line) < line.qta
        })
        .map(([id]) => ITEM_BY_ID.get(id)?.name ?? ''),
    [cart],
  )

  const resetOrder = () => {
    setCart(new Map())
    setNumeroTavolo('')
    setTavolo('')
    setNotaOrdine('')
    setQrDataUrl(null)
    setPayload('')
    setView('menu')
  }

  const firstRender = useRef(true)

  // i browser mobile ripristinano la posizione di scroll della visita
  // precedente: lo disattiviamo, la pagina parte sempre dall'alto
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual'
  }, [])

  useEffect(() => {
    const behavior: ScrollBehavior =
      firstRender.current ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth'
    firstRender.current = false
    mainRef.current?.scrollTo({ top: 0, behavior })
    window.scrollTo({ top: 0, behavior })
  }, [view])

  useEffect(() => {
    if (view !== 'ticket') return
    const text = buildOrderPayload(numeroTavolo.trim(), rows, {
      cliente: tavolo.trim(),
      note: notaOrdine,
    })
    setPayload(text)
    let cancelled = false
    QRCode.toDataURL(text, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 640,
      color: { dark: '#1c1e1a', light: '#f4efe3' },
    })
      .then((url) => !cancelled && setQrDataUrl(url))
      .catch(() => !cancelled && setQrDataUrl(null))
    return () => {
      cancelled = true
    }
  }, [view, numeroTavolo, tavolo, rows, notaOrdine])

  return (
    <div ref={mainRef} className="min-h-dvh bg-paper text-ink font-body">
      {view === 'menu' && (
        <MenuView
          cart={cart}
          total={total}
          itemCount={itemCount}
          missingMods={missingMods}
          onChangeQty={changeQty}
          onAddMod={addMod}
          onRemoveMod={removeMod}
          onModCount={setModCount}
          onContinue={() => setView('details')}
        />
      )}
      {view === 'details' && (
        <DetailsView
          numeroTavolo={numeroTavolo}
          tavolo={tavolo}
          notaOrdine={notaOrdine}
          rows={rows}
          total={total}
          onNumeroTavolo={setNumeroTavolo}
          onTavolo={setTavolo}
          onNotaOrdine={setNotaOrdine}
          onBack={() => setView('menu')}
          onGenerate={() => setView('ticket')}
        />
      )}
      {view === 'ticket' && (
        <TicketView
          numeroTavolo={numeroTavolo}
          tavolo={tavolo}
          notaOrdine={notaOrdine}
          rows={rows}
          total={total}
          qrDataUrl={qrDataUrl}
          payload={payload}
          onNewOrder={resetOrder}
        />
      )}
    </div>
  )
}

/* ────────────────────────────── MENU ────────────────────────────── */

function Header({ eyebrow }: { eyebrow: string }) {
  return (
    <header className="border-b-2 border-ink/80 pb-5 pt-8">
      <div className="flex items-center gap-4">
        <img
          src="/logo.png"
          alt="Stemma Sagra di Rovereto"
          className="h-16 w-16 shrink-0 rounded-full ring-2 ring-ink/80"
        />
        <div>
          <p className="font-accent italic text-field-deep text-base leading-tight">{eyebrow}</p>
          <h1 className="font-display font-semibold uppercase leading-[0.95] tracking-tight text-3xl">
            Sagra di Rovereto
          </h1>
          <p className="font-display uppercase tracking-[0.25em] text-ink-soft text-xs mt-1">Preordine</p>
        </div>
      </div>
    </header>
  )
}

function MenuView({
  cart,
  total,
  itemCount,
  missingMods,
  onChangeQty,
  onAddMod,
  onRemoveMod,
  onModCount,
  onContinue,
}: {
  cart: Cart
  total: number
  itemCount: number
  missingMods: string[]
  onChangeQty: (id: number, delta: number) => void
  onAddMod: (id: number, ingredientId: number) => void
  onRemoveMod: (id: number, ingredientId: number) => void
  onModCount: (id: number, ingredientId: number, count: number) => void
  onContinue: () => void
}) {
  return (
    <div className="mx-auto max-w-xl px-5 pb-8">
      <Header eyebrow="Un menu per tavolo" />
      <p className="mt-4 text-sm leading-relaxed text-ink-soft">
        Scegli i piatti, genera il codice e mostralo in cassa: la comanda arriva diretta ai reparti
        di cucina. Sviluppato da{' '}
        <a
          href="https://novaui.it/it"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-field-deep underline decoration-field-deep/40 underline-offset-2 transition-colors hover:text-ink hover:decoration-ink"
        >
          NovaUI
        </a>
      </p>

      <nav
        aria-label="Categorie"
        className="sticky top-0 -mx-5 z-10 mt-5 bg-paper/95 backdrop-blur px-5 py-3 border-b border-ink/15"
      >
        <div className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {MENU.map((cat) => (
            <a
              key={cat.key}
              href={`#cat-${cat.key}`}
              className="shrink-0 rounded-full border border-ink/60 px-3.5 py-1.5 font-display uppercase text-xs tracking-wider text-ink transition-colors hover:bg-ink hover:text-paper focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-field"
            >
              {cat.label}
            </a>
          ))}
        </div>
      </nav>

      {MENU.map((cat) => (
        <section key={cat.key} id={`cat-${cat.key}`} className="scroll-mt-20 pt-7">
          <div className="flex items-baseline gap-3">
            <h2 className="font-display font-semibold uppercase text-xl tracking-tight">{cat.label}</h2>
            {cat.note && (
              <span className="font-accent italic text-sm text-field-deep">{cat.note}</span>
            )}
          </div>
          <ul className="mt-2 divide-y divide-ink/10 border-y border-ink/10">
            {cat.items.map((item) => (
              <DishRow
                key={item.id}
                item={item}
                line={cart.get(item.id)}
                onChange={onChangeQty}
                onAddMod={onAddMod}
                onRemoveMod={onRemoveMod}
                onModCount={onModCount}
              />
            ))}
          </ul>
        </section>
      ))}

      <p className="mt-8 font-accent italic text-sm text-ink-soft">
        In caso di allergie ed intolleranze, chiedere in cassa.
      </p>

      {/* sticky e non fixed: su iOS le barre fixed saltano a metà pagina
          quando il contenuto cambia altezza (es. selezione pillole);
          bottom-5 mantiene il margine sotto la barra come prima */}
      <div className="sticky bottom-5 z-20 mt-8">
        {missingMods.length > 0 && (
          <p className="mb-2 rounded-xl border border-stamp/50 bg-paper px-4 py-2.5 text-sm font-medium text-stamp shadow-md shadow-ink/10">
            Completa la scelta per: {missingMods.join(', ')}
          </p>
        )}
        <div className="flex items-stretch gap-3 rounded-2xl border-2 border-ink bg-ink p-3 text-paper shadow-xl shadow-ink/30">
          <div className="flex flex-col justify-center pl-2">
            <span className="font-display text-2xl font-semibold leading-none">{formatEuro(total)}</span>
            <span className="mt-1 text-[11px] uppercase tracking-wider text-paper/60">
              {itemCount > 0 ? `${itemCount} ${itemCount === 1 ? 'portata' : 'portate'}` : 'nessuna portata'}
            </span>
          </div>
          <button
            type="button"
            disabled={rowsEmpty(cart) || missingMods.length > 0}
            onClick={onContinue}
            className="ml-auto rounded-xl bg-amber px-6 font-display text-base font-semibold uppercase tracking-wider text-ink transition-all hover:bg-paper focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber disabled:opacity-30 disabled:saturate-0"
          >
            Riepilogo
          </button>
        </div>
      </div>
    </div>
  )
}

function rowsEmpty(cart: Cart): boolean {
  return ![...cart.values()].some((line) => line.qta > 0)
}

function DishRow({
  item,
  line,
  onChange,
  onAddMod,
  onRemoveMod,
  onModCount,
}: {
  item: MenuItem
  line: CartLine | undefined
  onChange: (id: number, delta: number) => void
  onAddMod: (id: number, ingredientId: number) => void
  onRemoveMod: (id: number, ingredientId: number) => void
  onModCount: (id: number, ingredientId: number, count: number) => void
}) {
  const qta = line?.qta ?? 0
  const mods = line?.mods ?? []
  const assigned = assignedCount({ qta, mods })
  const free = qta - assigned
  const aria = STEP_ARIA(item.name)
  return (
    <li className="py-3.5">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-medium leading-snug">{item.name}</p>
          {item.detail && <p className="font-accent italic text-sm text-ink-soft">{item.detail}</p>}
        </div>
        <span className="shrink-0 font-display text-base font-medium tabular-nums">
          {formatEuro(item.price)}
        </span>
        <div className="flex shrink-0 items-center gap-1" role="group" aria-label={`Quantità ${item.name}`}>
          <StepperButton label={aria.minus} disabled={qta === 0} onClick={() => onChange(item.id, -1)}>
            −
          </StepperButton>
          <span
            aria-live="polite"
            className={`w-7 text-center font-display text-lg font-semibold tabular-nums transition-colors ${
              qta > 0 ? 'text-field-deep' : 'text-ink/30'
            }`}
          >
            {qta > 0 ? qta : '·'}
          </span>
          <StepperButton label={aria.plus} onClick={() => onChange(item.id, 1)}>
            +
          </StepperButton>
        </div>
      </div>
      {qta > 0 && item.modifiers && (
        <div className="mt-2.5 pl-1">
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label={`Modifiche per ${item.name}`}>
            {item.modifiers.map((m) => {
              const mod = mods.find((x) => x.ingredientId === m.ingredientId)
              if (!mod) {
                return (
                  <button
                    key={m.ingredientId}
                    type="button"
                    aria-pressed={false}
                    disabled={free <= 0}
                    onClick={() => onAddMod(item.id, m.ingredientId)}
                    className="rounded-full border border-ink/40 px-3.5 py-1.5 text-xs font-medium text-ink-soft transition-colors hover:border-ink hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-field disabled:opacity-30"
                  >
                    {m.label}
                  </button>
                )
              }
              return (
                <div
                  key={m.ingredientId}
                  className="flex items-center gap-2 rounded-full border border-field-deep bg-field-deep py-1 pl-3.5 pr-1.5 text-xs font-medium text-paper"
                >
                  <span>✓ {m.label}</span>
                  <span className="flex items-center gap-1" role="group" aria-label={`Quante con ${m.label}`}>
                    <button
                      type="button"
                      aria-label={`Meno ${m.label}`}
                      disabled={mod.count <= 1}
                      onClick={() => onModCount(item.id, m.ingredientId, mod.count - 1)}
                      className="flex h-6 w-6 items-center justify-center rounded-full text-base font-semibold leading-none transition-colors hover:bg-paper/20 disabled:opacity-30"
                    >
                      −
                    </button>
                    <span aria-live="polite" className="w-8 text-center font-display text-sm font-semibold tabular-nums">
                      {mod.count} di {qta}
                    </span>
                    <button
                      type="button"
                      aria-label={`Più ${m.label}`}
                      disabled={mod.count >= qta - (assigned - mod.count)}
                      onClick={() => onModCount(item.id, m.ingredientId, mod.count + 1)}
                      className="flex h-6 w-6 items-center justify-center rounded-full text-base font-semibold leading-none transition-colors hover:bg-paper/20 disabled:opacity-30"
                    >
                      +
                    </button>
                  </span>
                  <button
                    type="button"
                    aria-label={`Rimuovi ${m.label}`}
                    onClick={() => onRemoveMod(item.id, m.ingredientId)}
                    className="flex h-6 w-6 items-center justify-center rounded-full text-sm transition-colors hover:bg-paper/20"
                  >
                    ✕
                  </button>
                </div>
              )
            })}
          </div>
          {item.modRequired && free > 0 && (
            <p className="mt-1.5 text-xs font-medium text-stamp">
              {free === qta
                ? `${item.modPrompt ?? 'Scegli la correzione'}: ne ${qta === 1 ? 'serve' : 'servono'} ${qta}`
                : `Ne ${free === 1 ? 'manca' : 'mancano'} ancora ${free} da assegnare`}
            </p>
          )}
        </div>
      )}
    </li>
  )
}

function StepperButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-ink text-xl font-semibold leading-none transition-colors hover:bg-ink hover:text-paper focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-field disabled:border-ink/20 disabled:text-ink/20 disabled:hover:bg-transparent disabled:hover:text-ink/20"
    >
      {children}
    </button>
  )
}

/* ─────────────────────────── RIEPILOGO ─────────────────────────── */

function DetailsView({
  numeroTavolo,
  tavolo,
  notaOrdine,
  rows,
  total,
  onNumeroTavolo,
  onTavolo,
  onNotaOrdine,
  onBack,
  onGenerate,
}: {
  numeroTavolo: string
  tavolo: string
  notaOrdine: string
  rows: ExpandedRow[]
  total: number
  onNumeroTavolo: (v: string) => void
  onTavolo: (v: string) => void
  onNotaOrdine: (v: string) => void
  onBack: () => void
  onGenerate: () => void
}) {
  const canGenerate = numeroTavolo.trim().length > 0 && tavolo.trim().length > 0 && rows.length > 0

  return (
    <div className="mx-auto max-w-xl px-5 pb-16">
      <Header eyebrow="Quasi fatto" />

      <div className="mt-6 flex gap-3">
        <label className="block w-32 shrink-0">
          <span className="font-display text-xs font-medium uppercase tracking-[0.2em] text-ink-soft">
            N° tavolo <span className="text-stamp">*</span>
          </span>
          <input
            value={numeroTavolo}
            onChange={(e) => onNumeroTavolo(e.target.value)}
            placeholder="es. 12"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            className="mt-1.5 w-full rounded-xl border-2 border-ink/60 bg-transparent px-4 py-3 font-medium tabular-nums placeholder:text-ink/30 focus:border-field focus:outline-none"
          />
        </label>
        <label className="block min-w-0 flex-1">
          <span className="font-display text-xs font-medium uppercase tracking-[0.2em] text-ink-soft">
            Nome del tavolo <span className="text-stamp">*</span>
          </span>
          <input
            value={tavolo}
            onChange={(e) => onTavolo(e.target.value)}
            placeholder="es. Beppe"
            inputMode="text"
            autoComplete="off"
            className="mt-1.5 w-full rounded-xl border-2 border-ink/60 bg-transparent px-4 py-3 font-medium placeholder:text-ink/30 focus:border-field focus:outline-none"
          />
        </label>
      </div>

      <h2 className="mt-8 font-display font-semibold uppercase text-xl tracking-tight">L'ordine</h2>
      <ul className="mt-2 divide-y divide-ink/10 border-y border-ink/10">
        {rows.map((r, i) => {
          const item = ITEM_BY_ID.get(r.id)
          if (!item) return null
          return (
            <li key={`${r.id}-${i}`} className="py-2.5 text-sm">
              <div className="flex items-baseline gap-3">
                <span className="w-8 shrink-0 font-display font-semibold tabular-nums text-field-deep">
                  {r.qta}×
                </span>
                <span className="min-w-0 flex-1 font-medium">{r.name}</span>
                <span className="shrink-0 font-display tabular-nums">{formatEuro(item.price * r.qta)}</span>
              </div>
              {r.note && (
                <p className="mt-0.5 pl-11 font-accent italic text-ink-soft">{r.note}</p>
              )}
            </li>
          )
        })}
      </ul>

      <label className="mt-6 block">
        <span className="font-display text-xs font-medium uppercase tracking-[0.2em] text-ink-soft">
          Note per la cucina
        </span>
        <textarea
          value={notaOrdine}
          onChange={(e) => onNotaOrdine(e.target.value)}
          placeholder="facoltative, valgono per tutto l'ordine"
          rows={2}
          className="mt-1.5 w-full resize-none rounded-xl border-2 border-ink/60 bg-transparent px-4 py-3 font-medium placeholder:text-ink/30 focus:border-field focus:outline-none"
        />
      </label>

      <p className="mt-4 flex items-baseline justify-between">
        <span className="font-display uppercase tracking-[0.2em] text-xs text-ink-soft">Totale</span>
        <span className="font-display text-3xl font-semibold">{formatEuro(total)}</span>
      </p>

      <div className="mt-8 flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="rounded-xl border-2 border-ink px-6 py-3.5 font-display font-semibold uppercase tracking-wider transition-colors hover:bg-ink hover:text-paper focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-field"
        >
          Indietro
        </button>
        <button
          type="button"
          disabled={!canGenerate}
          onClick={onGenerate}
          className="flex-1 rounded-xl bg-ink px-6 py-3.5 font-display font-semibold uppercase tracking-wider text-paper transition-colors hover:bg-field-deep focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-field disabled:opacity-30"
        >
          Genera codice
        </button>
      </div>
      {!canGenerate && (
        <p className="mt-3 text-sm text-stamp">Inserisci numero e nome del tavolo per generare il codice.</p>
      )}
    </div>
  )
}

/* ─────────────────────────── SCONTRINO ─────────────────────────── */

function TicketView({
  numeroTavolo,
  tavolo,
  notaOrdine,
  rows,
  total,
  qrDataUrl,
  payload,
  onNewOrder,
}: {
  numeroTavolo: string
  tavolo: string
  notaOrdine: string
  rows: ExpandedRow[]
  total: number
  qrDataUrl: string | null
  payload: string
  onNewOrder: () => void
}) {
  return (
    <div className="min-h-dvh bg-ink px-5 py-10">
      <div className="mx-auto max-w-sm">
        <div className="overflow-hidden rounded-t-sm bg-paper shadow-2xl shadow-black/50">
          <div className="perf-top" style={{ ['--ticket-bg' as string]: '#1c1e1a' }} />
          <div className="px-7 pb-4 pt-2 text-center">
            <img src="/logo.png" alt="" className="mx-auto h-14 w-14 rounded-full" />
            <p className="mt-2 font-display font-semibold uppercase leading-tight tracking-tight">
              Sagra di Rovereto
            </p>
            <p className="font-accent italic text-sm text-ink-soft">
              Tavolo {numeroTavolo}
              {tavolo.trim() ? ` · ${tavolo.trim()}` : ''}
            </p>
          </div>

          <div className="mx-7 border-t-2 border-dashed border-ink/30" />

          <ul className="px-7 py-4">
            {rows.map((r, i) => {
              const item = ITEM_BY_ID.get(r.id)
              if (!item) return null
              return (
                <li key={`${r.id}-${i}`} className="py-1 text-sm">
                  <div className="flex items-baseline gap-2">
                    <span className="w-7 shrink-0 font-display font-semibold tabular-nums">{r.qta}×</span>
                    <span className="min-w-0 flex-1 font-medium">{r.name}</span>
                    <span className="shrink-0 font-display tabular-nums">{formatEuro(item.price * r.qta)}</span>
                  </div>
                  {r.note && (
                    <p className="pl-9 font-accent italic text-ink-soft">{r.note}</p>
                  )}
                </li>
              )
            })}
          </ul>

          {notaOrdine.trim() && (
            <>
              <div className="mx-7 border-t-2 border-dashed border-ink/30" />
              <p className="px-7 py-3 text-sm">
                <span className="font-display text-xs uppercase tracking-[0.2em] text-ink-soft">Note · </span>
                <span className="font-accent italic">{notaOrdine.trim()}</span>
              </p>
            </>
          )}

          <div className="mx-7 border-t-2 border-dashed border-ink/30" />

          <p className="flex items-baseline justify-between px-7 py-4">
            <span className="font-display uppercase tracking-[0.25em] text-xs text-ink-soft">Totale</span>
            <span className="font-display text-4xl font-semibold tracking-tight">{formatEuro(total)}</span>
          </p>

          <div className="px-7 pb-5">
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt="Codice ordine da mostrare in cassa"
                className="w-full rounded-lg border-2 border-ink"
              />
            ) : (
              <div className="flex aspect-square w-full items-center justify-center rounded-lg border-2 border-ink/20 font-accent italic text-ink-soft">
                Sto preparando il codice…
              </div>
            )}
            <p className="mt-3 text-center font-display text-sm font-semibold uppercase tracking-[0.2em]">
              Mostra questo codice in cassa
            </p>
          </div>
        </div>
        <div className="zigzag-bottom" aria-hidden="true" />

        {/* il timbro: unico accento rosso della pagina */}
        <div className="mt-8 flex justify-center">
          <span className="-rotate-6 rounded-lg border-4 border-stamp px-5 py-2 font-display text-xl font-semibold uppercase tracking-[0.15em] text-stamp opacity-90">
            Da pagare in cassa
          </span>
        </div>

        <button
          type="button"
          onClick={onNewOrder}
          className="mt-10 w-full rounded-xl border-2 border-paper/40 py-3.5 font-display font-semibold uppercase tracking-wider text-paper transition-colors hover:bg-paper hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber"
        >
          Nuovo ordine
        </button>

        {payload && (
          <details className="mt-6 text-paper/40">
            <summary className="cursor-pointer text-center font-display text-xs uppercase tracking-widest">
              Dettagli tecnici
            </summary>
            <pre className="mt-2 max-h-40 overflow-auto rounded-lg bg-black/30 p-3 text-[10px] leading-relaxed break-all">
              {decodeURIComponent(payload)}
            </pre>
          </details>
        )}
      </div>
    </div>
  )
}

export default PreordiniPage
